import { Center, SimpleGrid } from '@mantine/core';
import StatCard from '@/components/StatCard.tsx';
import useRetrieveAutomationRunStats from '@/hooks/api/runs/useRetrieveAutomationRunsStats.ts';
import CustomSpinner from '@/components/Spinner.tsx';
import StatusIndicator from '@/components/StatusIndicator.tsx';
import { ExecutionStatus } from '@/types/statuses.ts';
import { isDefaultRunStatusSelection } from '@/types/runs.ts';

type props = {
  filterStatuses: ExecutionStatus[];
  toggleStatus: (status: ExecutionStatus) => void;
  onlyStatus: (status: ExecutionStatus) => void;
  resetStatuses: () => void;
};

const cols = 3;

const RunsStats = ({ filterStatuses, toggleStatus, onlyStatus, resetStatuses }: props) => {
  const { data: stats } = useRetrieveAutomationRunStats();

  const allStatusesSelected = isDefaultRunStatusSelection(filterStatuses);
  const isStatusActive = (status: ExecutionStatus) =>
    !allStatusesSelected && filterStatuses.includes(status);
  const selectStatus = (status: ExecutionStatus) => {
    if (allStatusesSelected) {
      onlyStatus(status);
      return;
    }

    toggleStatus(status);
  };

  return (
    <Center>
      {!stats ? (
        <CustomSpinner />
      ) : (
        <SimpleGrid cols={cols} w={'100%'} spacing={'sm'}>
          <StatCard
            title="All"
            value={stats.runs_count}
            active={allStatusesSelected}
            onClick={resetStatuses}
            icon={<StatusIndicator status={ExecutionStatus.All} include_text={false} />}
            bg={'white'}
          />
          <StatCard
            title="In progress"
            value={stats.in_progress_count}
            active={isStatusActive(ExecutionStatus.Active)}
            onClick={() => selectStatus(ExecutionStatus.Active)}
            icon={
              <StatusIndicator
                status={ExecutionStatus.Active}
                include_text={false}
                useSpinner={false}
              />
            }
            bg={'white'}
          />
          <StatCard
            title="Needs input"
            value={stats.ask_user_count}
            active={isStatusActive(ExecutionStatus.AskUser)}
            onClick={() => selectStatus(ExecutionStatus.AskUser)}
            icon={
              <StatusIndicator
                status={ExecutionStatus.AskUser}
                include_text={false}
                useSpinner={false}
              />
            }
            bg={'white'}
          />
          <StatCard
            title="Completed"
            value={stats.completed_count}
            active={isStatusActive(ExecutionStatus.Completed)}
            onClick={() => selectStatus(ExecutionStatus.Completed)}
            icon={<StatusIndicator status={ExecutionStatus.Completed} include_text={false} />}
            bg={'white'}
          />
          <StatCard
            title="Completed with issues"
            value={stats.completed_with_issues_count}
            active={isStatusActive(ExecutionStatus.PartiallyCompleted)}
            onClick={() => selectStatus(ExecutionStatus.PartiallyCompleted)}
            icon={
              <StatusIndicator
                status={ExecutionStatus.PartiallyCompleted}
                include_text={false}
                useSpinner={false}
              />
            }
            bg={'white'}
          />
          <StatCard
            title="Failed"
            value={stats.failed_count}
            active={isStatusActive(ExecutionStatus.Errors)}
            onClick={() => selectStatus(ExecutionStatus.Errors)}
            icon={<StatusIndicator status={ExecutionStatus.Errors} include_text={false} />}
            bg={'white'}
          />
        </SimpleGrid>
      )}
    </Center>
  );
};

export default RunsStats;
